import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, Loader2, CheckCircle2, ShieldCheck, Check, X } from "lucide-react";

// Password validation helpers

const hasMinLength = (pw: string) => pw.length >= 8;
const hasLowercase = (pw: string) => /[a-z]/.test(pw);
const hasUppercase = (pw: string) => /[A-Z]/.test(pw);
const hasNumber = (pw: string) => /[0-9]/.test(pw);
const hasSpecialChar = (pw: string) => /[^a-zA-Z0-9]/.test(pw);

const ResetPassword = () => {
  const navigate = useNavigate();
  const { signOut, isPasswordRecovery } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isReady, setIsReady] = useState(false);
  // Supabase sends an expired or already-used link back here with an error in the
  // URL and no session, so "Verifying your link" would otherwise never finish.
  const [linkFailed] = useState(() =>
    new URLSearchParams(window.location.hash.slice(1)).has("error_code"),
  );

  useEffect(() => {
    // If AuthContext already detected PASSWORD_RECOVERY, we're ready
    if (isPasswordRecovery) {
      setIsReady(true);
      return;
    }

    // Listen for the PASSWORD_RECOVERY event from Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsReady(true);
      }
    });

    // Also check if user is already in a recovery session
    // (e.g. if the page was refreshed after clicking the link)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setIsReady(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [isPasswordRecovery]);

  // Real-time validation checks
  const checks = useMemo(() => ({
    minLength: hasMinLength(newPassword),
    hasLowercase: hasLowercase(newPassword),
    hasUppercase: hasUppercase(newPassword),
    hasNumber: hasNumber(newPassword),
    hasSpecial: hasSpecialChar(newPassword),
    passwordsMatch: newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword,
  }), [newPassword, confirmPassword]);

  const allChecksPassed = checks.minLength && checks.hasLowercase && checks.hasUppercase && checks.hasNumber && checks.hasSpecial && checks.passwordsMatch;

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!allChecksPassed) {
      toast.error("Please meet all password requirements");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
        // Mark that this account has finished setting its own password. The
        // internal onboarding wizard gates "Account created" on this flag so it
        // only advances once the client has actually completed setup (not just
        // opened the link). Harmless for normal password-reset users.
        data: { onboarding_password_set: true },
      });

      if (error) {
        // Supabase returns this when the new password is the same as the current one
        if (error.message?.toLowerCase().includes("same_password") ||
            error.message?.toLowerCase().includes("different from the old password") ||
            error.message?.toLowerCase().includes("should be different")) {
          toast.error("New password cannot be the same as your current password");
          return;
        }
        throw error;
      }

      setIsComplete(true);
      toast.success("Password updated successfully!");

      // Sign the user out so they can log in fresh with their new password
      await signOut();
    } catch (error: any) {
      toast.error(error.message || "Failed to update password");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/auth", { replace: true });
  };

  // Validation check item component
  const CheckItem = ({ passed, label }: { passed: boolean; label: string }) => (
    <div className="flex items-center gap-2">
      {passed ? (
        <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
      ) : (
        <X className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
      )}
      <span className={`text-xs ${passed ? "text-green-600" : "text-muted-foreground/60"}`}>
        {label}
      </span>
    </div>
  );

  // Success state
  if (isComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-border/50 text-center animate-in fade-in duration-300">
          <CardHeader className="pt-10 pb-6">
            <div className="mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-6 ring-8 bg-green-500/10 ring-green-500/5">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            </div>
            <CardTitle className="text-3xl font-heading font-bold tracking-tight text-foreground">
              Password Updated!
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Your password has been successfully changed. Please sign in with your new password.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-8 pb-10">
            <Button className="w-full" onClick={handleBackToLogin}>
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isReady && linkFailed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-border/50 text-center">
          <CardHeader className="pt-10 pb-6">
            <div className="mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-6 ring-8 bg-muted ring-muted/40">
              <Clock className="w-12 h-12 text-muted-foreground" />
            </div>
            <CardTitle className="text-2xl font-heading font-bold tracking-tight text-foreground">
              This link has expired
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Setup and password links only work once, for a limited time. We can email you a new one.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-8 pb-10">
            <Button className="w-full" onClick={() => navigate("/auth?forgot=1", { replace: true })}>
              Get a new link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading / waiting for recovery token
  if (!isReady) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-border/50 text-center">
          <CardHeader className="pt-10 pb-6">
            <div className="mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-6 ring-8 bg-primary/10 ring-primary/5">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
            </div>
            <CardTitle className="text-2xl font-heading font-bold tracking-tight text-foreground">
              Verifying your link...
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Please wait while we verify your password reset link.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-border/50 animate-in fade-in duration-300">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ring-4 bg-primary/10 ring-primary/5">
            <ShieldCheck className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading font-bold">Set new password</CardTitle>
          <CardDescription>Enter your new password below</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isSubmitting}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">Confirm new password</Label>
              <Input
                id="confirm-new-password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
                autoComplete="new-password"
              />
            </div>

            {/* Password requirements checklist */}
            <div className="space-y-1.5 pt-1">
              <CheckItem passed={checks.minLength} label="At least 8 characters" />
              <CheckItem passed={checks.hasLowercase} label="Contains a lowercase letter (a-z)" />
              <CheckItem passed={checks.hasUppercase} label="Contains an uppercase letter (A-Z)" />
              <CheckItem passed={checks.hasNumber} label="Contains a number (0-9)" />
              <CheckItem passed={checks.hasSpecial} label="Contains a special character (!@#$...)" />
              <CheckItem passed={checks.passwordsMatch} label="Passwords match" />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting || !allChecksPassed}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
